// @ts-check
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import LazyHydrate from 'react-lazy-hydration';
import { Link } from '@codex/basics';
import { hasNav } from '../helpers';
import BackDrop from '../BackDrop';
import MenuDefault, { MenuDefaultWrapper } from './MenuDefault';
import MenuServices, { MenuServicesWrapper } from './MenuServices';

const NavigationListWrapper = styled.div`
  display: none;
  min-height: 45px;
  background-color: ${({ theme }) => theme.colors.link.default};
  padding: 0 20px;
  margin-top: 10px;

  @media ${({ theme }) => theme.breakpoints.desktop} {
    display: block;
  }
`;

const Separator = styled.li`
  flex: 1;
`;

const NavigationItemLink = styled(Link)`
  display: flex;
  font-size: 14px;
  font-weight: ${({ theme }) => (theme.name === 'currys' ? 'bold' : 'normal')};
  padding: 14px 10px;
  color: inherit;
  flex-direction: column;
  align-items: center;

  &:hover {
    text-decoration: none;
    background-color: ${({ theme }) => theme.colors.shades[7]};
    color: ${({ theme }) => theme.colors.link.default};
    font-weight: bold;
  }

  &::after {
    content: "${props => props.children}";
    content: "${props => props.children}" / "";
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    font-weight: bold;

    @media speech {
      display: none;
    }
  }
`;

const NavigationItem = styled.li`
  position: relative;
  color: ${({ theme }) => theme.colors.shades[7]};

  &:hover {
    background-color: ${({ theme }) => theme.colors.shades[7]};
    color: ${({ theme }) => theme.colors.link.default};

    ${MenuDefaultWrapper} {
      display: flex;
    }

    ${MenuServicesWrapper} {
      display: block;
    }
  }
`;

const NavigationList = styled.ul`
  display: flex;
  position: relative;
  z-index: 4;
  justify-content: space-between;
  list-style: none;
  margin: 0 auto;
  max-width: 1240px;
  padding: 0;

  ${NavigationItem}:last-child {
    background-color: ${({ theme }) => theme.colors.header.nav.mark.background};

    ${MenuDefaultWrapper} {
      display: block;
      left: auto;
      right: 0;
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.shades[7]};
      color: ${({ theme }) => theme.colors.link.default};
    }
  }
`;

const DesktopHeaderNavigation = (props) => {
  const [navActive, setNavActive] = useState(false);
  const [index, setIndex] = useState(0);
  const refNavigation = useRef(null);

  const positionTop =
    refNavigation.current !== null &&
    refNavigation.current.offsetTop + refNavigation.current.offsetHeight;

  const handleActiveLink = (i, isActive) => {
    setNavActive(isActive);
    setIndex(i);
  };

  const renderNavigationItem = (items, type) => {
    if (!items) {
      return null;
    }
    return items.nav.map((item, i) => {
      const indexType = `${type}-${i}`;

      return (
        <NavigationItem
          key={indexType}
          onMouseEnter={() => hasNav(item) && handleActiveLink(indexType, true)}
          onMouseLeave={() => setNavActive(false)}
          style={{ position: item.style === 'services' && 'static' }}
        >
          <NavigationItemLink href={item.link}>{item.label}</NavigationItemLink>
          <LazyHydrate whenVisible>
            {hasNav(item) &&
              navActive &&
              indexType === index &&
              (item.style === 'services' ? (
                <MenuServices data={item.nav} />
              ) : (
                <MenuDefault data={item.nav} />
              ))}
          </LazyHydrate>
        </NavigationItem>
      );
    });
  };

  return (
    <NavigationListWrapper>
      {navActive && <BackDrop positionTop={positionTop} />}
      <NavigationList ref={refNavigation}>
        {props.navigationData && (
          <>
            {renderNavigationItem(props.navigationData.catalog, 'catalog')}
            <Separator />
            {renderNavigationItem(props.navigationData.pages, 'pages')}
          </>
        )}
      </NavigationList>
    </NavigationListWrapper>
  );
};

DesktopHeaderNavigation.propTypes = {
  navigationData: PropTypes.shape({
    catalog: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      )
    }),
    pages: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      )
    })
  })
};

DesktopHeaderNavigation.defaultProps = {
  navigationData: undefined
};

export default DesktopHeaderNavigation;
